import React, { useState, useEffect, useCallback, memo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ArrowLeft, 
  Clock, 
  AlertTriangle,
  ChevronDown,
  Menu,
  X
} from 'lucide-react';
import { StatusChip, PriorityChip, SLABadge } from '@/components/phs/StatusChip';
import CommunicationsHub from '@/components/phs/CommunicationsHub';
import TakeActionPanel from '@/components/phs/TakeActionPanel';
import MobileStickyActionBar from '@/components/phs/MobileStickyActionBar';
import EvidenceUploadPanel from '@/components/phs/EvidenceUploadPanel';
import EvidenceReviewPanel from '@/components/phs/EvidenceReviewPanel';
import LeftOperationalPanel from '@/components/phs/LeftOperationalPanel';
import { computeWorkflowStatus } from '@/components/phs/WorkflowStatus';
import { PHSCommandLayout } from '@/components/phs/enterprise';
import { withWyldecrestAuth } from '@/pages/wyldecrest/WyldecrestAuthGuard';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useIsMobile } from '@/hooks/usePerformance';

const API = process.env.REACT_APP_BACKEND_URL;

// Skeleton loader for job workspace
function JobWorkspaceSkeleton() {
  return (
    <div className="flex h-full gap-4 p-4">
      {/* Left Column Skeleton */}
      <div className="w-72 flex-shrink-0 space-y-4">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-40 rounded-2xl" />
        <Skeleton className="h-32 rounded-2xl" />
      </div>
      {/* Center Column Skeleton */}
      <div className="flex-1 min-w-0">
        <Skeleton className="h-full rounded-2xl" />
      </div>
      {/* Right Column Skeleton */}
      <div className="w-72 flex-shrink-0 space-y-4">
        <Skeleton className="h-64 rounded-2xl" />
        <Skeleton className="h-48 rounded-2xl" />
      </div>
    </div>
  );
}

// Main Job Workspace Component - MOBILE OPTIMIZED
function PHSJobWorkspace() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  // C2 FIX: Track specific actions to prevent duplicates
  const [activeAction, setActiveAction] = useState(null);
  // Mobile sidebar visibility
  const [showMobileContext, setShowMobileContext] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [showMobileEvidence, setShowMobileEvidence] = useState(false);
  
  // Detect mobile
  const isMobile = useIsMobile();
  
  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    if (jobId) {
      loadJob();
    }
  }, [jobId]);
  
  const loadJob = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/wyldecrest/jobs/${jobId}`, { headers });
      setJob(res.data);
    } catch (err) {
      console.error('Failed to load job:', err);
      toast.error('Failed to load job');
    } finally {
      setLoading(false);
    }
  }, [jobId, headers]);
  
  // C3 FIX: All actions now refresh job state after completion
  const handleAction = useCallback(async (actionId, jobData) => {
    // C2 FIX: Prevent duplicate actions
    if (activeAction === actionId) {
      return;
    }
    
    setActiveAction(actionId);
    setActionLoading(true);
    
    try {
      // Handle different actions
      switch (actionId) {
        case 'add_note':
          // Scroll to communications on mobile
          if (isMobile) {
            document.getElementById('comms-section')?.scrollIntoView({ behavior: 'smooth' });
          }
          // Focus on communication input
          document.querySelector('[data-testid="comms-input"]')?.focus();
          break;
        case 'chase':
          await axios.post(`${API}/api/phs/jobs/${jobId}/communications`, {
            type: 'contractor_message',
            message: 'Please provide an update on this job.',
            visibility: 'contractor'
          }, { headers });
          toast.success('Chase message sent');
          await loadJob(); // C3: Refresh state
          break;
        case 'approve_quote':
          await axios.patch(`${API}/api/wyldecrest/jobs/${jobId}`, {
            quote_status: 'approved',
            status: 'Quote Approved'
          }, { headers });
          toast.success('Quote approved');
          await loadJob(); // C3: Refresh state
          break;
        case 'assign':
        case 'reassign':
          // Navigate to enterprise job workspace which has assignment modal
          navigate(`/phs/helpdesk/job/${jobId}`);
          break;
        case 'mark_awaiting':
          await axios.patch(`${API}/api/wyldecrest/jobs/${jobId}`, {
            status: 'Awaiting Quote'
          }, { headers });
          toast.success('Status updated to Awaiting Quote');
          await loadJob(); // C3: Refresh state
          break;
        case 'reject_quote':
          await axios.patch(`${API}/api/wyldecrest/jobs/${jobId}`, {
            quote_status: 'rejected',
            status: 'Quote Rejected'
          }, { headers });
          toast.success('Quote rejected');
          await loadJob(); // C3: Refresh state
          break;
        case 'cancel':
          await axios.patch(`${API}/api/wyldecrest/jobs/${jobId}`, {
            status: 'Cancelled'
          }, { headers });
          toast.success('Job cancelled');
          await loadJob(); // C3: Refresh state
          break;
        case 'escalate':
          await axios.patch(`${API}/api/wyldecrest/jobs/${jobId}`, {
            priority: 'Emergency',
            escalated: true
          }, { headers });
          toast.success('Job escalated');
          await loadJob(); // C3: Refresh state
          break;
        case 'close_job':
          await axios.patch(`${API}/api/wyldecrest/jobs/${jobId}`, {
            status: 'Closed'
          }, { headers });
          toast.success('Job closed');
          await loadJob(); // C3: Refresh state
          break;
        default:
          // Handle unrecognized actions - log for debugging
          console.warn(`Unhandled action: ${actionId}`);
          await loadJob();
      }
    } catch (err) {
      console.error('Action failed:', err);
      // Handle rate limiting error specifically
      if (err.response?.status === 429) {
        toast.error(err.response?.data?.detail || 'Rate limit exceeded. Please wait before sending more messages.');
      } else {
        toast.error(err.response?.data?.detail || 'Action failed');
      }
    } finally {
      setActionLoading(false);
      setActiveAction(null);
    }
  }, [activeAction, jobId, headers, loadJob, isMobile]);
  
  const handleCommunicationAdded = (comm) => {
    setJob(prev => ({
      ...prev,
      communications: [comm, ...(prev.communications || [])]
    }));
  };
  
  const handleContactAction = async (action) => {
    try {
      await axios.post(`${API}/api/phs/jobs/${jobId}/communications`, action, { headers });
    } catch (err) {
      console.error('Failed to log contact action:', err);
    }
  };
  
  // Mobile upload evidence handler
  const handleUploadEvidence = useCallback(() => {
    // Scroll to evidence section and expand
    document.getElementById('evidence-section')?.scrollIntoView({ behavior: 'smooth' });
    toast.info('Use the Evidence Upload panel above');
  }, []);
  
  // Mobile open comms handler
  const handleOpenComms = useCallback(() => {
    document.getElementById('comms-section')?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  
  return (
    <PHSCommandLayout>
      <div className={`h-[calc(100vh-64px)] flex flex-col ${isMobile ? 'pb-28' : ''}`} data-testid="phs-job-workspace">
        {/* Header Bar - MOBILE OPTIMIZED */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 px-3 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="gap-1 px-2 sm:px-3 h-8"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div className="h-5 w-px bg-slate-200 hidden sm:block" />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-sm sm:text-lg font-semibold text-slate-800 truncate">
                    {job?.reference || job?.id?.slice(0, 8) || 'Loading...'}
                  </span>
                  {job && <StatusChip status={job.status} size="xs" />}
                </div>
                <div className="text-[10px] sm:text-xs text-slate-500 truncate">
                  {job?.job_type || job?.category || 'Job'} • {job?.park_name || job?.park || ''}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Mobile Context Toggle */}
              {isMobile && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowMobileContext(!showMobileContext)}
                  className="h-8 px-2"
                >
                  <Menu className="h-4 w-4" />
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadJob} 
                disabled={loading}
                className="h-8 px-2 sm:px-3"
              >
                <Clock className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>
          
          {/* Mobile Quick Context Bar */}
          {isMobile && job && (
            <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-100 text-[10px] overflow-x-auto">
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-slate-400">SLA:</span>
                <SLABadge job={job} size="xs" />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-slate-400">Awaiting:</span>
                <span className="font-medium text-slate-600">{computeWorkflowStatus(job).awaiting_with}</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <PriorityChip priority={job.priority} size="xs" />
              </div>
            </div>
          )}
        </div>
        
        {/* Mobile Context Drawer */}
        {isMobile && showMobileContext && job && (
          <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setShowMobileContext(false)}>
            <div 
              className="absolute left-0 top-0 bottom-0 w-80 bg-white overflow-y-auto animate-in slide-in-from-left-full duration-200"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                <h3 className="font-semibold text-slate-800">Job Details</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowMobileContext(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-4">
                <ErrorBoundary componentName="LeftOperationalPanel" minimal>
                  <LeftOperationalPanel job={job} onContactAction={handleContactAction} />
                </ErrorBoundary>
              </div>
            </div>
          </div>
        )}
        
        {/* 3-Column Layout (Desktop) / Stacked Layout (Mobile) */}
        {loading ? (
          <JobWorkspaceSkeleton />
        ) : !job ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center px-4">
              <AlertTriangle className="h-10 w-10 sm:h-12 sm:w-12 text-slate-300 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-slate-700">Job not found</h3>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">The requested job could not be loaded.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop: 3-Column Layout */}
            <div className="hidden lg:flex flex-1 gap-4 p-4 overflow-hidden">
              {/* LEFT COLUMN - Operational Context */}
              <ErrorBoundary componentName="LeftOperationalPanel" minimal>
                <LeftOperationalPanel job={job} onContactAction={handleContactAction} />
              </ErrorBoundary>
              
              {/* CENTER COLUMN - Communications Hub */}
              <div className="flex-1 min-w-0 bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col">
                <ErrorBoundary componentName="CommunicationsHub">
                  <CommunicationsHub
                    jobId={job.id}
                    communications={job.communications || []}
                    onCommunicationAdded={handleCommunicationAdded}
                    className="flex-1 !rounded-none !border-0"
                  />
                </ErrorBoundary>
              </div>
              
              {/* RIGHT COLUMN - Evidence & Workflow Actions */}
              <div className="w-80 flex-shrink-0 space-y-4 overflow-y-auto">
                {/* Evidence Upload Panel */}
                <ErrorBoundary componentName="EvidenceUploadPanel" minimal>
                  <EvidenceUploadPanel
                    jobId={job.id}
                    existingEvidence={job.evidence_files || []}
                    requiredCategories={['before', 'after']}
                    onUploadComplete={loadJob}
                  />
                </ErrorBoundary>
                
                {/* Evidence Review Panel - For Internal Ops */}
                <ErrorBoundary componentName="EvidenceReviewPanel" minimal>
                  <EvidenceReviewPanel
                    job={job}
                    onReviewComplete={loadJob}
                  />
                </ErrorBoundary>
                
                {/* Workflow Actions */}
                <ErrorBoundary componentName="TakeActionPanel" minimal>
                  <TakeActionPanel
                    job={job}
                    onAction={handleAction}
                    onUploadEvidence={handleUploadEvidence}
                    loading={actionLoading}
                  />
                </ErrorBoundary>
              </div>
            </div>
            
            {/* Mobile: Stacked Layout */}
            <div className="flex-1 lg:hidden overflow-y-auto overscroll-contain">
              {/* Evidence Upload Panel (Mobile) */}
              <div id="evidence-section" className="p-3 bg-slate-50 border-b border-slate-200">
                <ErrorBoundary componentName="EvidenceUploadPanel" minimal>
                  <EvidenceUploadPanel
                    jobId={job.id}
                    existingEvidence={job.evidence_files || []}
                    requiredCategories={['before', 'after']}
                    onUploadComplete={loadJob}
                  />
                </ErrorBoundary>
              </div>
              
              {/* Communications Hub (Primary on mobile) */}
              <div id="comms-section" className="min-h-[50vh] bg-white border-b border-slate-200">
                <ErrorBoundary componentName="CommunicationsHub">
                  <CommunicationsHub
                    jobId={job.id}
                    communications={job.communications || []}
                    onCommunicationAdded={handleCommunicationAdded}
                    className="h-full"
                  />
                </ErrorBoundary>
              </div>
              
              {/* Collapsible Actions Section */}
              <div className="p-3 bg-slate-50">
                <button
                  onClick={() => setShowMobileActions(!showMobileActions)}
                  className="w-full flex items-center justify-between p-3 bg-white rounded-xl border border-slate-200 active:bg-slate-50 touch-manipulation"
                >
                  <span className="text-sm font-medium text-slate-700">Workflow Actions</span>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition ${showMobileActions ? 'rotate-180' : ''}`} />
                </button>
                
                {showMobileActions && (
                  <div className="mt-3 animate-in slide-in-from-top-2 duration-200">
                    <ErrorBoundary componentName="TakeActionPanel" minimal>
                      <TakeActionPanel
                        job={job}
                        onAction={handleAction}
                        onUploadEvidence={handleUploadEvidence}
                        loading={actionLoading}
                      />
                    </ErrorBoundary>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        
        {/* Mobile Sticky Action Bar */}
        {isMobile && job && (
          <MobileStickyActionBar
            job={job}
            onAction={handleAction}
            onUploadEvidence={handleUploadEvidence}
            onAddNote={() => handleAction('add_note')}
            onOpenComms={handleOpenComms}
            loading={actionLoading}
          />
        )}
      </div>
    </PHSCommandLayout>
  );
}

export default withWyldecrestAuth(PHSJobWorkspace, 'helpdesk');
